import type {
  GrilleCellDto,
  GrilleViewDto,
  RateSeniorityLevelDto,
} from '@/api/rate-collection-client';
import { SENIORITY_LEVELS_ORDERED } from '@/lib/rate-collection-labels';

// =============================================================================
// saisie-model.ts — View-model de la grille de saisie (profil = ligne, niveau = colonne).
// =============================================================================
// Transforme la liste plate GrilleViewDto.cells (1 entrée par couple profil×niveau) en
// LIGNES par profil (4 colonnes de niveau). La cardinalité suit le scope réel (POINT 2 acté :
// « 82×4 » = borne d'AFFICHAGE, jamais un plafond) — AUCUN cap/troncature/pagination dure.
// Pur calcul (aucun effet de bord, aucune règle métier).

/** Clé canonique d'une cellule (couple profil×niveau) — alignée sur le contrat de route. */
export function cellKey(
  profileCode: string,
  seniorityLevel: RateSeniorityLevelDto,
): string {
  return `${profileCode}::${seniorityLevel}`;
}

/** Une ligne de la grille : un profil + ses 4 cellules indexées par niveau. */
export interface SaisieRow {
  readonly profileCode: string;
  readonly profileLabel: string | null;
  /**
   * Famille du profil — donnée DÉRIVÉE du catalog FIGÉ portée par le DTO (`cell.family`), source
   * de vérité UNIQUE (D2-bis, OPP-03). JAMAIS un parsing de libellé. `null` = profil hors catalog
   * (regroupé sous « Sans famille » à l'affichage, sans tronquer la grille).
   */
  readonly family: string | null;
  /** Cellules indexées par niveau de séniorité (toujours les 4 niveaux). */
  readonly cells: Readonly<Record<RateSeniorityLevelDto, GrilleCellDto>>;
}

/**
 * Construit les lignes de la grille depuis la vue serveur. Regroupe les cellules par profil ;
 * complète les niveaux absents par une cellule Vide synthétique (robustesse : le serveur trie
 * et fournit normalement les 4 niveaux, mais la grille ne doit jamais « trouer »).
 */
export function buildRows(view: GrilleViewDto): readonly SaisieRow[] {
  const byProfile = new Map<
    string,
    {
      label: string | null;
      family: string | null;
      cells: Map<RateSeniorityLevelDto, GrilleCellDto>;
    }
  >();

  // Préserve l'ordre d'apparition des profils (le serveur trie par profileCode). La famille est
  // PORTÉE par le DTO (cell.family) — source de vérité unique, jamais dérivée du libellé (D2-bis).
  for (const cell of view.cells) {
    let entry = byProfile.get(cell.profileCode);
    if (entry === undefined) {
      entry = { label: cell.profileLabel, family: cell.family, cells: new Map() };
      byProfile.set(cell.profileCode, entry);
    }
    entry.cells.set(cell.seniorityLevel, cell);
  }

  const rows: SaisieRow[] = [];
  for (const [profileCode, entry] of byProfile) {
    const cells = {} as Record<RateSeniorityLevelDto, GrilleCellDto>;
    for (const level of SENIORITY_LEVELS_ORDERED) {
      cells[level] =
        entry.cells.get(level) ??
        emptyCell(profileCode, entry.label, entry.family, level);
    }
    rows.push({
      profileCode,
      profileLabel: entry.label,
      family: entry.family,
      cells,
    });
  }
  return rows;
}

/** Cellule Vide synthétique (niveau absent de la vue serveur — ne troue jamais la grille). */
function emptyCell(
  profileCode: string,
  profileLabel: string | null,
  family: string | null,
  seniorityLevel: RateSeniorityLevelDto,
): GrilleCellDto {
  return {
    profileCode,
    profileLabel,
    family,
    seniorityLevel,
    // Cellule synthétique pour un niveau absent de la vue serveur : ciblée par défaut (ne sur-grise
    // jamais un couple que le serveur n'a simplement pas listé). Le ciblage réel vient du DTO serveur.
    targeted: true,
    tjmValueEur: null,
    cellState: 'Vide',
    lastSavedAt: null,
    cycle1ValueEur: null,
  };
}

/** Familles distinctes présentes dans la grille, triées (« Sans famille » en dernier). */
export function distinctFamilies(rows: readonly SaisieRow[]): readonly string[] {
  const set = new Set<string>();
  let hasUnfamilied = false;
  for (const row of rows) {
    if (row.family === null) {
      hasUnfamilied = true;
    } else {
      set.add(row.family);
    }
  }
  const sorted = [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  return hasUnfamilied ? [...sorted, UNFAMILIED] : sorted;
}

/**
 * S35-CLONE-SKILLS-UX #7 (DBU §3.5 l.2862 : « Dernière sauvegarde : il y a X minutes »). Libellé RELATIF
 * FR d'un horodatage de sauvegarde. PUR (`nowMs` injecté ⇒ déterministe/testable). `< 1 min` ⇒ « il y a
 * moins d'une minute » ; sinon « il y a X minute(s) ». `null`/invalide ⇒ chaîne vide (l'appelant décide du
 * défaut). Accord grammatical (1 minute / X minutes).
 */
export function relativeSaveLabel(iso: string | null, nowMs: number): string {
  if (iso === null) {
    return '';
  }
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) {
    return '';
  }
  const diffMin = Math.floor((nowMs - t) / 60_000);
  if (diffMin <= 0) {
    return "il y a moins d'une minute";
  }
  if (diffMin === 1) {
    return 'il y a 1 minute';
  }
  return `il y a ${String(diffMin)} minutes`;
}

/**
 * Maquette #85 (point 3) — libellé de l'indicateur « Sauvegardé … » (GRIS, à gauche du CTA). Règle
 * d'affichage : minutes si < 1 h (« Sauvegardé il y a N min » / « à l'instant ») ; heure seule si le
 * jour même (« Sauvegardé à HH:MM ») ; date sinon (« Sauvegardé le JJ mois à HH:MM »). PUR (`nowMs`
 * injecté ⇒ déterministe/testable). `null`/invalide ⇒ chaîne vide (l'appelant décide du défaut).
 */
export function formatLastSaved(iso: string | null, nowMs: number): string {
  if (iso === null) {
    return '';
  }
  const d = new Date(iso);
  const t = d.getTime();
  if (Number.isNaN(t)) {
    return '';
  }
  const diffMin = Math.floor((nowMs - t) / 60_000);
  if (diffMin < 60) {
    if (diffMin <= 0) {
      return 'Sauvegardé à l’instant';
    }
    return `Sauvegardé il y a ${String(diffMin)} min`;
  }
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const now = new Date(nowMs);
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return `Sauvegardé à ${time}`;
  }
  const date = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return `Sauvegardé le ${date} à ${time}`;
}

/** Pseudo-famille pour les profils sans famille dérivable (FLAG OPP-03). */
export const UNFAMILIED = '__sans_famille__';

/** Libellé d'affichage d'une famille (UNFAMILIED → « Sans famille »). */
export function familyDisplayLabel(family: string): string {
  return family === UNFAMILIED ? 'Sans famille' : family;
}

// F-10 (S17, per-couple, option B) : PLUS d'index batch. Chaque cellule émet sa propre query
// verdictant son couple contre le TJM SAUVEGARDÉ (use-positioning · usePositioningCouple). Le
// lookup O(1) par cellule est devenu le cache TanStack keyé `[positioning, campaignId, p, l, tjm]`.

/** Filtre les lignes par famille (`undefined`/'' = toutes). Pur (aucune mutation). */
export function filterRowsByFamily(
  rows: readonly SaisieRow[],
  family: string | undefined,
): readonly SaisieRow[] {
  if (family === undefined || family === '') {
    return rows;
  }
  if (family === UNFAMILIED) {
    return rows.filter((r) => r.family === null);
  }
  return rows.filter((r) => r.family === family);
}

// =============================================================================
// SF2/SF3/SF4/SF5 (DBU §3.5) — récapitulatif de CONTRÔLE, totalisation et avancement.
// =============================================================================
// Tout est PUR (aucun effet de bord, aucune règle métier, aucune requête). Les agrégats (SF4)
// sont CONFIDENTIELS : calculés côté front sur les seules valeurs saisies par CE fournisseur,
// JAMAIS transmis au serveur (cloisonnement / K-anonymity — l'agrégation inter-fournisseurs
// est l'affaire du worker analytics, hors de cette grille). Cellules vides = EXCLUES.

/**
 * Résolveur de la valeur COURANTE d'une cellule (couple profil×niveau). L'écran l'implémente
 * depuis l'état d'autosave en mémoire (valeur saisie/sauvegardée), `null` = cellule vide. Ce seam
 * garde le modèle pur et découplé du moteur d'autosave.
 */
export type CellValueResolver = (
  profileCode: string,
  seniorityLevel: RateSeniorityLevelDto,
) => number | null;

/** Agrégats confidentiels d'une famille (SF4). Cellules vides exclues. `null` si aucune valeur. */
export interface FamilyAggregate {
  /** Nombre de couples (profil×niveau) renseignés dans la famille. */
  readonly count: number;
  readonly sumEur: number;
  readonly meanEur: number | null;
  readonly medianEur: number | null;
}

/**
 * Médiane d'une liste de nombres (code maison ~5 lignes, PAS de lib stats). Liste vide → null.
 * Moyenne des deux valeurs centrales si le cardinal est pair. N'altère pas l'entrée (copie triée).
 */
export function median(values: readonly number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const midValue = sorted[mid] ?? 0;
  if (sorted.length % 2 === 1) {
    return midValue;
  }
  const prev = sorted[mid - 1] ?? 0;
  return (prev + midValue) / 2;
}

/** Valeurs renseignées (non nulles) des cellules d'une ligne, via le résolveur. */
function rowValues(
  row: SaisieRow,
  resolve: CellValueResolver,
): readonly number[] {
  const values: number[] = [];
  for (const level of SENIORITY_LEVELS_ORDERED) {
    const value = resolve(row.profileCode, level);
    if (value !== null) {
      values.push(value);
    }
  }
  return values;
}

/** SF4 — agrégats (somme + moyenne + médiane) d'une famille, cellules vides exclues. */
export function familyAggregate(
  rows: readonly SaisieRow[],
  resolve: CellValueResolver,
): FamilyAggregate {
  const values: number[] = [];
  for (const row of rows) {
    values.push(...rowValues(row, resolve));
  }
  const sumEur = values.reduce((acc, v) => acc + v, 0);
  return {
    count: values.length,
    sumEur,
    meanEur: values.length === 0 ? null : sumEur / values.length,
    medianEur: median(values),
  };
}

/** G16 — agrégats d'UNE ligne (profil) : moyenne + médiane de ses taux saisis. `null` si aucun. */
export interface RowAggregate {
  readonly meanEur: number | null;
  readonly medianEur: number | null;
}

/**
 * G16 (relevé Gate Human 2026-09-15) — moyenne + médiane des taux SAISIS d'un profil (une ligne de la
 * grille), cellules vides exclues. Rendu en bout de chaque ligne du tableau de saisie. `null` sur chaque
 * agrégat si le profil n'a aucun taux saisi. PUR : réutilise `rowValues` (mêmes cellules que l'agrégat de
 * famille) + le helper `median` maison — aucune lib stats, aucune règle métier, aucune requête.
 */
export function rowAggregate(
  row: SaisieRow,
  resolve: CellValueResolver,
): RowAggregate {
  const values = rowValues(row, resolve);
  return {
    meanEur:
      values.length === 0
        ? null
        : values.reduce((acc, v) => acc + v, 0) / values.length,
    medianEur: median(values),
  };
}

/**
 * S35-CLONE-SKILLS-UX #7 (DBU §3.5 l.2876 : « Mise en évidence des profils encore vides ou partiellement
 * complétés »). Complétude d'un profil DÉRIVÉE du CIBLAGE per-couple (A0-9) : seuls les niveaux CIBLÉS
 * (`cell.targeted !== false`) comptent au dénominateur — un couple non concerné (grisé) n'est JAMAIS un
 * « trou » à combler. `empty` = aucun niveau ciblé renseigné ; `partial` = certains mais pas tous ;
 * `complete` = tous les niveaux ciblés renseignés (ou aucun niveau ciblé → rien à saisir).
 */
export type ProfileCompletion = 'empty' | 'partial' | 'complete';

/** Classe la complétude d'un profil (SF5/#7). PUR : réutilise le résolveur de valeur courante. */
export function profileCompletion(
  row: SaisieRow,
  resolve: CellValueResolver,
): ProfileCompletion {
  let targeted = 0;
  let entered = 0;
  for (const level of SENIORITY_LEVELS_ORDERED) {
    // Un couple NON concerné (grisé, A0-9) est hors décompte : il ne peut ni manquer ni compléter.
    if (row.cells[level].targeted === false) {
      continue;
    }
    targeted += 1;
    if (resolve(row.profileCode, level) !== null) {
      entered += 1;
    }
  }
  if (entered === 0) {
    return 'empty';
  }
  return entered < targeted ? 'partial' : 'complete';
}

/**
 * SF5 — « profil saisi » = AU MOINS un niveau ciblé renseigné (≥ 1 valeur non nulle). Le résolveur
 * ne renvoie une valeur que pour les niveaux réellement ciblés/renseignés (cellules vides = null).
 */
export function isProfileEntered(
  row: SaisieRow,
  resolve: CellValueResolver,
): boolean {
  return rowValues(row, resolve).length > 0;
}

/** Avancement (SF5) : nombre de profils saisis sur le total. */
export interface ProgressCount {
  readonly entered: number;
  readonly total: number;
}

/** SF5 — avancement global ou par sous-ensemble de lignes (profils saisis / total). */
export function progressCount(
  rows: readonly SaisieRow[],
  resolve: CellValueResolver,
): ProgressCount {
  let entered = 0;
  for (const row of rows) {
    if (isProfileEntered(row, resolve)) {
      entered += 1;
    }
  }
  return { entered, total: rows.length };
}

/**
 * Maquette #85 (point 16) — avancement au grain TAUX (cellule) : « X / Y taux ». Compte les couples
 * CIBLÉS (`targeted !== false`, A0-9) renseignés sur le total des couples ciblés. Un couple non concerné
 * n'entre NI au numérateur NI au dénominateur (il n'est pas un « taux à saisir »). PUR.
 */
export function cellProgressCount(
  rows: readonly SaisieRow[],
  resolve: CellValueResolver,
): ProgressCount {
  let entered = 0;
  let total = 0;
  for (const row of rows) {
    for (const level of SENIORITY_LEVELS_ORDERED) {
      if (row.cells[level].targeted === false) {
        continue;
      }
      total += 1;
      if (resolve(row.profileCode, level) !== null) {
        entered += 1;
      }
    }
  }
  return { entered, total };
}

/** Un couple (profil × niveau) restant à saisir (détail de la tuile Avancement, maquette #85 point 16). */
export interface MissingCell {
  readonly profileCode: string;
  readonly profileLabel: string;
  readonly level: RateSeniorityLevelDto;
}

/**
 * Maquette #85 (point 16) — liste des couples CIBLÉS encore VIDES (« N taux à saisir · <profil, niveau> »).
 * Ordre de la grille (profils puis niveaux). PUR : réutilise le résolveur de valeur courante.
 */
export function missingCells(
  rows: readonly SaisieRow[],
  resolve: CellValueResolver,
): readonly MissingCell[] {
  const out: MissingCell[] = [];
  for (const row of rows) {
    for (const level of SENIORITY_LEVELS_ORDERED) {
      if (row.cells[level].targeted === false) {
        continue;
      }
      if (resolve(row.profileCode, level) === null) {
        out.push({
          profileCode: row.profileCode,
          profileLabel: row.profileLabel ?? row.profileCode,
          level,
        });
      }
    }
  }
  return out;
}

/**
 * Avancement par famille (DBU §3.5 « jauge d'avancement par famille » du menu de gauche). `key` =
 * clé d'URL de filtre (nom de famille ou `UNFAMILIED` pour « Sans famille »), `label` = libellé
 * d'affichage, `progress` = profils saisis / total DANS la famille. Ordre `distinctFamilies`
 * (« Sans famille » en dernier). PUR : réutilise le résolveur de valeur courante.
 */
export interface FamilyProgress {
  readonly key: string;
  readonly label: string;
  readonly progress: ProgressCount;
}

/** Construit l'avancement par famille (jauge du menu de gauche, DBU §3.5). Pur. */
export function buildFamilyProgress(
  rows: readonly SaisieRow[],
  resolve: CellValueResolver,
): readonly FamilyProgress[] {
  return distinctFamilies(rows).map((key) => {
    const familyRows =
      key === UNFAMILIED
        ? rows.filter((r) => r.family === null)
        : rows.filter((r) => r.family === key);
    return {
      key,
      label: familyDisplayLabel(key),
      progress: progressCount(familyRows, resolve),
    };
  });
}

/** Récapitulatif de contrôle d'une famille (SF2/SF3) : agrégats + complétude + profils vides. */
export interface FamilyControlSummary {
  readonly family: string | null;
  readonly familyLabel: string;
  readonly aggregate: FamilyAggregate;
  readonly progress: ProgressCount;
  /**
   * G17 (relevé Gate Human 2026-09-15) — avancement au grain TAUX de la famille : taux renseignés /
   * total des taux CIBLÉS (cellules non concernées exclues). Seul indicateur chiffré du Contrôler
   * depuis G17 (la moyenne et la médiane n'y figurent plus ; elles passent en bout de ligne, G16).
   */
  readonly cellProgress: ProgressCount;
  /** Profils SANS aucune valeur (mise en évidence SF2) — libellés d'affichage. */
  readonly emptyProfiles: readonly { code: string; label: string }[];
  /**
   * #7 (DBU l.2876) : profils PARTIELLEMENT complétés — certains niveaux ciblés renseignés, d'autres non
   * (distincts des profils vides). Mise en évidence dédiée au Contrôler. Libellés d'affichage.
   */
  readonly partialProfiles: readonly { code: string; label: string }[];
}

/** Regroupe les lignes par famille (ordre distinctFamilies : « Sans famille » en dernier). */
export function buildControlSummaries(
  rows: readonly SaisieRow[],
  resolve: CellValueResolver,
): readonly FamilyControlSummary[] {
  const families = distinctFamilies(rows);
  const summaries: FamilyControlSummary[] = [];
  for (const familyKey of families) {
    const familyRows =
      familyKey === UNFAMILIED
        ? rows.filter((r) => r.family === null)
        : rows.filter((r) => r.family === familyKey);
    const display = (r: SaisieRow): { code: string; label: string } => ({
      code: r.profileCode,
      label: r.profileLabel ?? r.profileCode,
    });
    // #7 (DBU l.2876) : distingue VIDES (aucune valeur) et PARTIELS (certains niveaux ciblés seulement).
    const emptyProfiles = familyRows
      .filter((r) => profileCompletion(r, resolve) === 'empty')
      .map(display);
    const partialProfiles = familyRows
      .filter((r) => profileCompletion(r, resolve) === 'partial')
      .map(display);
    summaries.push({
      family: familyKey === UNFAMILIED ? null : familyKey,
      familyLabel: familyDisplayLabel(familyKey),
      aggregate: familyAggregate(familyRows, resolve),
      progress: progressCount(familyRows, resolve),
      cellProgress: cellProgressCount(familyRows, resolve),
      emptyProfiles,
      partialProfiles,
    });
  }
  return summaries;
}

// =============================================================================
// SF8 (DBU §3.5) — écart Cycle 1 ↔ Cycle 2, DÉRIVÉ front (per couple profil×niveau).
// =============================================================================
// PUR (aucun effet de bord, aucune requête). L'écart n'est calculé QUE si les DEUX côtés existent
// (valeur C1 rappelée par l'API + valeur COURANTE C2 saisie). WCAG 2.1 AA : le sens (hausse/baisse/
// stable) est porté par un LIBELLÉ TEXTE explicite (`label`), jamais par la seule couleur ni la seule
// flèche. Le composant double le libellé d'une icône directionnelle ; le texte reste l'autorité a11y.

/** Sens de l'écart C1→C2 (SF8). `equal` = valeur inchangée entre les deux cycles. */
export type Cycle1DiffDirection = 'up' | 'down' | 'equal';

/** Écart dérivé C1→C2 d'un couple (SF8). `null` renvoyé par `computeCycle1Diff` s'il n'y a rien à comparer. */
export interface Cycle1Diff {
  readonly direction: Cycle1DiffDirection;
  /** Écart absolu en euros (courant − Cycle 1). Signé (négatif = baisse). */
  readonly deltaEur: number;
  /**
   * Écart relatif en pourcentage arrondi (courant vs Cycle 1). Signé. `null` si la valeur Cycle 1 est
   * 0 (pourcentage indéfini — jamais de division par zéro).
   */
  readonly deltaPercent: number | null;
  /** Libellé TEXTE accessible (WCAG : porte le sens ; jamais la couleur/flèche seule). */
  readonly label: string;
}

/**
 * SF8 — écart Cycle 1 → valeur COURANTE (Cycle 2) d'un couple. Retourne `null` s'il n'y a rien à
 * comparer (pas de rappel Cycle 1 OU cellule courante vide) : l'UI n'affiche alors aucun indicateur.
 */
export function computeCycle1Diff(
  cycle1ValueEur: number | null,
  currentValueEur: number | null,
): Cycle1Diff | null {
  if (cycle1ValueEur === null || currentValueEur === null) {
    return null;
  }
  const deltaEur = currentValueEur - cycle1ValueEur;
  const deltaPercent =
    cycle1ValueEur === 0 ? null : Math.round((deltaEur / cycle1ValueEur) * 100);
  const direction: Cycle1DiffDirection =
    deltaEur > 0 ? 'up' : deltaEur < 0 ? 'down' : 'equal';
  return { direction, deltaEur, deltaPercent, label: cycle1DiffLabel(direction, deltaEur, deltaPercent) };
}

/** Libellé FR de l'écart (SF8). Ex. « En hausse : +50 € (+11 %) vs Cycle 1 » / « Inchangé vs Cycle 1 ». */
function cycle1DiffLabel(
  direction: Cycle1DiffDirection,
  deltaEur: number,
  deltaPercent: number | null,
): string {
  if (direction === 'equal') {
    return 'Inchangé vs Cycle 1';
  }
  const word = direction === 'up' ? 'En hausse' : 'En baisse';
  const eurSign = deltaEur > 0 ? '+' : '-';
  const eurPart = `${eurSign}${String(Math.abs(deltaEur))} €`;
  const pctPart =
    deltaPercent === null
      ? ''
      : ` (${deltaPercent > 0 ? '+' : '-'}${String(Math.abs(deltaPercent))} %)`;
  return `${word} : ${eurPart}${pctPart} vs Cycle 1`;
}
